import { InjectionToken } from '@angular/core';
import type { Observable } from 'rxjs';
import type { CardEvent } from '../model/card.ws';
import type {
  Card,
  CardOperation,
  CardSensitiveData,
  CardStatistics,
  CardTopupQuote,
  CardTransaction,
  CardTransactionDirection,
  CardType,
  FundingFeeQuote,
  Paginated,
  ThreeDsCode,
} from '../model/card.types';

export interface IssueCardRequest {
  walletId: string;
  currencyCode: string;
  cardName: string;
  /** CARD-002: required for every card type — the wallet-confirmation code is mailed here. */
  email: string;
  /** CARD-002: the exact type picked from `available-types`; the price is computed from it server-side. */
  vendorId: string;
  paymentSystem?: string;
}

export interface TopupRequest {
  walletId: string;
  sourceAmountRaw: bigint;
}

export interface QuoteTopupRequest {
  sourceAmountRaw?: bigint;
  fiatAmountMinor?: bigint;
}

export interface ListCardsParams {
  take?: number;
  skip?: number;
  status?: string;
  currencyCode?: string;
}

export interface ListOperationsParams {
  take?: number;
  skip?: number;
  status?: string;
  type?: string;
  cardId?: string;
}

export interface ListTransactionsParams {
  take?: number;
  skip?: number;
  cardId?: string;
  direction?: CardTransactionDirection;
  status?: string;
  dateFrom?: string;
  dateTo?: string;
  search?: string;
}

export interface StatisticsParams {
  dateFrom?: string;
  dateTo?: string;
}

export interface CardTransport {
  availableTypes(currencyCode?: string, paymentSystem?: string): Observable<CardType[]>;
  list(params?: ListCardsParams): Observable<Paginated<Card>>;
  get(cardId: string): Observable<Card>;
  issue(request: IssueCardRequest, idempotencyKey: string): Observable<CardOperation>;
  rename(cardId: string, cardName: string): Observable<Card>;
  setStatus(cardId: string, action: 'FREEZE' | 'UNFREEZE'): Observable<Card>;
  close(cardId: string): Observable<Card>;
  quoteTopup(cardId: string, request: QuoteTopupRequest): Observable<CardTopupQuote>;
  fundingFee(walletId: string, amountRaw?: bigint): Observable<FundingFeeQuote>;
  topup(cardId: string, request: TopupRequest, idempotencyKey: string): Observable<CardOperation>;
  operations(params?: ListOperationsParams): Observable<Paginated<CardOperation>>;
  transactions(params?: ListTransactionsParams): Observable<Paginated<CardTransaction>>;
  statistics(params?: StatisticsParams): Observable<CardStatistics>;
  sensitive(cardId: string, freshToken: string): Observable<CardSensitiveData>;
  walletApproval(cardId: string): Observable<void>;
  threeDsCodes(cardId: string): Observable<ThreeDsCode[]>;
  approveThreeDs(cardId: string, codeId: string): Observable<void>;
  events(): Observable<CardEvent>;
}

export const CARD_TRANSPORT = new InjectionToken<CardTransport>('CARD_TRANSPORT');

export interface CardEventSource {
  events(): Observable<CardEvent>;
}

export const CARD_EVENT_SOURCE = new InjectionToken<CardEventSource>('CARD_EVENT_SOURCE');
